import React, { useState, useEffect } from 'react';
import LayoutDefaultThemes from '../../../pages/LayoutEditorPage/meta/DefaultThemes';
import TableDefaultThemes from '../../../pages/TableEditorPage/meta/DefaultThemes';
import EditorDefaultThemes from '../DefaultThemes';
import Card from '../../../editor/ui/highed.card';
import UpsellCard from './UpsellCard';
import { getEditorConfig } from '../EditorHelper';
import Accordion from 'shared/Accordion';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import PanelContainer from './PanelContainer';
import { getThemesAction } from 'redux/actions/projectConfig';
import { assignThemeAction } from 'pages/ChartEditorPage/actions/chartEditor';
import { assignThemeAction as assignTableThemeAction } from 'pages/TableEditorPage/actions/tableEditor';
import { assignThemeAction as assignLayoutThemeAction } from 'pages/LayoutEditorPage/actions/layoutEditor';
import paths from 'paths';
import { checkPlanPermission } from 'utils/ProfileHelpers';

type ThemeProp = {
  onClick: (theme: object) => void;
  id: number;
};

type ThemesProp = {
  active: boolean;
};

const assignThemeMap = {
  chart: assignThemeAction,
  layout: assignLayoutThemeAction,
  table: assignTableThemeAction
};

const defaultThemesMap = {
  chart: EditorDefaultThemes,
  layout: LayoutDefaultThemes,
  table: TableDefaultThemes
};

export default (props: ThemesProp) => {
  const dispatch = useDispatch();
  const [showDefaults, setShowDefaults] = useState(true);
  const [selected, setSelected] = useState<boolean | number>(false);
  const { themeMeta } = useSelector<any, any>((state: RootState) => state.projectConfig);
  const { type, themes } = useSelector((state: RootState) => state.layoutEditorPage);
  const profile = useSelector((state: RootState) => state.profile);
  const { team, division } = profile;
  const [openSection, setOpenSection] = useState(themes.length ? 'custom' : 'default');
  const defaultThemes = defaultThemesMap[type as 'chart' | 'layout' | 'table'];
  const hasThemesPlanPermission = checkPlanPermission('custom_themes', profile);

  useEffect(() => {
    dispatch(getThemesAction({ team, division, type }));
    const anonymous = !team;
    getEditorConfig(team, division, anonymous).then((data) => {
      if (data && data.length) {
        setShowDefaults(!data[0].hide_default_themes);
      }
    });

    if (themeMeta && Object.keys(themeMeta).length > 1) {
      setSelected(themeMeta.id);
    } else {
      setSelected(defaultThemes[0].id);
    }
  }, []);

  const changeTheme = (theme: ThemeProp) => {
    dispatch(assignThemeMap[type as 'chart' | 'layout' | 'table']({ team: team ?? {}, theme }));
  };

  const toggleSection = (type: string) => setOpenSection(openSection === type ? '' : type);

  const onClickUpsell = () => {
    let path = paths.profileSubscription;
    if (hasThemesPlanPermission) path = paths.newTheme;
    window.open(path, '_blank');
  };

  return (
    <PanelContainer active={props.active} className="pt-6">
      <div className="flex flex-col gap-2 pb-4 ev-sm:pb-0">
        <Accordion
          headerText={'Your themes'}
          onClick={() => toggleSection('custom')}
          selected={openSection === 'custom'}
          childrenClasses={
            'relative flex flex-col rounded py-2 ev-sm:h-[calc(100vh-340px)] ev-sm:overflow-auto px-6 mr-2'
          }
        >
          <div className="project-grid-themes grid gap-4 grid-flow-row grid-cols-2">
            <UpsellCard
              title="YOUR THEME"
              text="Create a new theme"
              disabled={!hasThemesPlanPermission}
              onClick={onClickUpsell}
            />
            {themes.map((theme: ThemeProp, i: number) => {
              theme.onClick = () => {
                setSelected(theme.id);
                changeTheme(theme);
              };
              return (
                <div key={`custom_theme_${i}`}>
                  <Card {...theme} className={theme.id === selected ? 'active' : ''} />
                </div>
              );
            })}
          </div>
        </Accordion>
        {showDefaults && (
          <Accordion
            headerText={'Default themes'}
            onClick={() => toggleSection('default')}
            selected={openSection === 'default'}
            childrenClasses={
              'relative flex flex-col rounded py-2 ev-sm:h-[calc(100vh-340px)] ev-sm:overflow-auto px-6 mr-2'
            }
          >
            <div className="project-grid-themes grid gap-4 grid-flow-row grid-cols-2">
              {defaultThemes.map((theme, i) => {
                const clickableTheme = theme as unknown as ThemeProp;
                clickableTheme.onClick = () => {
                  setSelected(clickableTheme.id);
                  changeTheme(clickableTheme);
                };
                return (
                  <div key={`default_theme_${i}`}>
                    <Card {...theme} className={clickableTheme.id === selected ? 'active' : ''} />
                  </div>
                );
              })}
            </div>
          </Accordion>
        )}
      </div>
    </PanelContainer>
  );
};
